import type { WallItem } from "../shared/types";

export const wallScreenOrder = ["east", "south", "west", "north", "ceiling"] as const;
export type WallScreenName = (typeof wallScreenOrder)[number];

export const wallScreenCounts: Record<WallScreenName, number> = {
  // Roughly one guest card per four million physical pixels keeps the cards
  // at a comparable real-world size across the differently sized walls.
  east: 3,
  south: 10,
  west: 5,
  north: 5,
  ceiling: 8,
};

// The renderer needs more candidates than simultaneously visible positions;
// otherwise a departing card can only be replaced by itself. These pools are
// disjoint across the four walls, which keeps repetition between screens low.
const wallCandidateCounts: Record<Exclude<WallScreenName, "ceiling">, number> = {
  east: 5,
  south: 14,
  west: 6,
  north: 6,
};

const wallScreens = wallScreenOrder.filter(
  (screen): screen is Exclude<WallScreenName, "ceiling"> => screen !== "ceiling",
);

const hashString = (value: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const totalWallSlots = Object.values(wallCandidateCounts).reduce((sum, count) => sum + count, 0);
const totalVisibleWallSlots = wallScreens.reduce((sum, screen) => sum + wallScreenCounts[screen], 0);
const alwaysNewSlots = totalVisibleWallSlots;
const alwaysFeaturedSlots = 4;
const firstShowWindowMs = 5 * 60_000;
const immediateWindowMs = 15_000;

const publishedTime = (item: WallItem): number => {
  const parsed = Date.parse(item.publishedAt || item.createdAt);
  return Number.isFinite(parsed) ? parsed : 0;
};

const takeCircular = <T,>(items: T[], start: number, count: number): T[] => {
  if (!items.length || count <= 0) return [];
  const result: T[] = [];
  const seen = new Set<number>();
  for (let index = 0; index < count && seen.size < items.length; index += 1) {
    const itemIndex = (start + index) % items.length;
    if (seen.has(itemIndex)) continue;
    seen.add(itemIndex);
    result.push(items[itemIndex]);
  }
  return result;
};

export const selectGlobalWallItems = (
  items: WallItem[],
  rotationTick: number,
  now = Date.now(),
): WallItem[] => {
  if (!items.length) return [];

  const ranked = [...items].sort((left, right) => {
    if (left.currentShow !== right.currentShow) return left.currentShow ? -1 : 1;
    const publishedDifference = publishedTime(right) - publishedTime(left);
    if (publishedDifference) return publishedDifference;
    if (left.featured !== right.featured) return left.featured ? -1 : 1;
    return hashString(left.id) - hashString(right.id);
  });

  const currentShow = ranked.filter((item) => item.currentShow);
  const pool = currentShow.length >= totalWallSlots
    ? currentShow
    : [...currentShow, ...ranked.filter((item) => !item.currentShow)];

  const pinned: WallItem[] = [];
  const pinnedIds = new Set<string>();
  const pin = (item: WallItem) => {
    if (pinnedIds.has(item.id)) return;
    pinnedIds.add(item.id);
    pinned.push(item);
  };

  // A just-approved submission is inserted immediately. The reserved lane is
  // as large as every simultaneously visible position across the four walls,
  // so a normal approval reaches a real card slot on the next API poll. For
  // the following five minutes the lane rotates through burst overflow in
  // batches of 23 instead of leaving it behind the newest approvals.
  const fresh = pool.filter((item) => now - publishedTime(item) <= firstShowWindowMs);
  const immediate = fresh.filter((item) => now - publishedTime(item) <= immediateWindowMs);
  immediate.slice(0, alwaysNewSlots).forEach(pin);
  const freshBacklog = fresh.filter((item) => !pinnedIds.has(item.id));
  takeCircular(
    freshBacklog,
    rotationTick * alwaysNewSlots,
    Math.max(0, alwaysNewSlots - pinned.length),
  ).forEach(pin);
  pool.slice(0, alwaysNewSlots).forEach((item) => {
    if (pinned.length < alwaysNewSlots) pin(item);
  });

  // Featured is a frequency boost, not a permanent lock for the same four
  // records. Rotate the reserved featured lane so every admin-featured item
  // receives the boost while first-show items keep precedence.
  const featured = pool.filter((item) => item.featured && !pinnedIds.has(item.id));
  takeCircular(featured, rotationTick * alwaysFeaturedSlots, alwaysFeaturedSlots).forEach(pin);

  const backlog = pool.filter((item) => !pinnedIds.has(item.id));
  const rotatingSlots = Math.max(0, totalWallSlots - pinned.length);
  const pageCount = Math.max(1, Math.ceil(backlog.length / Math.max(1, rotatingSlots)));
  const page = ((rotationTick % pageCount) + pageCount) % pageCount;
  const rotating = takeCircular(backlog, page * rotatingSlots, rotatingSlots);

  return [...pinned, ...rotating].slice(0, Math.min(totalWallSlots, pool.length));
};

const assignWallCandidates = (
  selected: WallItem[],
): Map<Exclude<WallScreenName, "ceiling">, WallItem[]> => {
  const assignments = new Map(wallScreens.map((screen) => [screen, [] as WallItem[]]));

  const assign = (
    items: WallItem[],
    capacity: Record<Exclude<WallScreenName, "ceiling">, number>,
  ) => {
    // Rendezvous hashing gives every submission an independent, stable random
    // first choice while capacity spillover keeps the wall populations exact.
    for (const item of items) {
      const preferences = [...wallScreens].sort((left, right) => {
        const difference = hashString(`wall:${item.id}:${right}`) - hashString(`wall:${item.id}:${left}`);
        return difference || wallScreens.indexOf(left) - wallScreens.indexOf(right);
      });
      const screen = preferences.find((candidate) => (
        (assignments.get(candidate)?.length ?? 0) < capacity[candidate]
      ));
      if (screen) assignments.get(screen)!.push(item);
    }
  };

  // The selected array starts with the first-show lane. Fill the 23 physical
  // wall positions before using any of the eight off-screen replacement slots;
  // otherwise a fresh approval can be selected correctly but wait invisibly in
  // a candidate pool.
  assign(selected.slice(0, totalVisibleWallSlots), wallScreenCounts);
  assign(selected.slice(totalVisibleWallSlots), wallCandidateCounts);

  return assignments;
};

export const selectWallItems = (
  items: WallItem[],
  screen: WallScreenName,
  rotationTick: number,
  now = Date.now(),
): WallItem[] => {
  const selected = selectGlobalWallItems(items, rotationTick, now);
  if (screen === "ceiling") {
    const start = selected.length ? ((rotationTick % selected.length) + selected.length) % selected.length : 0;
    return takeCircular(selected, start, selected.length);
  }
  return assignWallCandidates(selected).get(screen) ?? [];
};
